import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { formatInvoiceNo, onboardingReminderTarget } from '@tenfour/contracts'
import type { NeedsYouSnoozeRecord } from '@tenfour/domain'
import {
  conversationActivityKind,
  conversationNeedsYouCandidate,
  invoiceAttention,
  isCustomerFacingConversation,
  isNeedsYouSnoozeActive,
  isReminderDue,
  isSetupClassReminderId,
  phonesMatch,
  sortNeedsYouItems,
} from '@tenfour/domain'
import { readSnoozed, writeSnoozed } from './needsYouSnooze'
import { useNeedsYouDismissals, useRecordNeedsYouDismissal } from '@/modules/needs-you/hooks'
import { useAuth, userCan } from '@/components/auth/AuthContext'
import { useAccount } from '@/modules/accounts/hooks'
import { useConversations } from '@/modules/conversations/hooks'
import type { Conversation } from '@/modules/conversations/api'
import {
  useAllInvoices,
  useApproveInvoice,
  useSendInvoiceReminder,
} from '@/modules/invoices/hooks'
import {
  useMarkAllRead,
  useMarkRead,
  useNotifications,
  useUnreadCount,
} from '@/modules/notifications/hooks'
import type { PortalNotification } from '@/modules/notifications/api'
import { useReminders, useUpdateReminder } from '@/modules/reminders/hooks'
import { useToast } from '@/components/layout/Toast'
import type { Reminder, WebInvoice } from '@/lib/types'
import { formatCurrency, formatPhone, formatRelative } from '@/lib/utils'

export type NeedsYouKind =
  | 'conversation'
  | 'relay'
  | 'handoff'
  | 'invoice_overdue'
  | 'invoice_review'
  | 'reminder'
  | 'notification'
  | 'profile_gap'

/**
 * One normalized triage item. `href` is the investigate path (whole-row tap);
 * `action` is the single inline resolution, and `ack` is the row's checkmark:
 * notifications/relays mark read, reminders toggle done, conversations and
 * invoices — which have no backend ack — snooze locally (per-account
 * localStorage), resurfacing on new activity or after 7 days.
 */
export interface NeedsYouItem {
  id: string
  kind: NeedsYouKind
  /** For conversation rows: was the latest touch a call or a text? Drives the icon. */
  conversationKind?: 'text' | 'call'
  /** Lets the expanded queue item render the live thread and reply composer. */
  conversationId?: string
  /** Existing list snapshot for customer/channel context in the decision pane. */
  conversation?: Conversation
  /** Full invoice snapshot for the invoice attention and line-item views. */
  invoice?: WebInvoice
  /** Full reminder snapshot for notes, recurrence, and linked-customer context. */
  reminder?: Reminder
  /** Full portal handoff snapshot for entity and message context. */
  notification?: PortalNotification
  tier: 1 | 2 | 3
  title: string
  sub?: string
  at: string
  href: string | null
  /** Fire-and-forget read acknowledgement for quiet notifications on open.
   *  Lyra relays stay active until reply/check completion. */
  onOpen?: () => void
  action?: {
    label: string
    href?: string
    /** Inline terminal actions call this only after their mutation succeeds. */
    run?: (onResolved?: () => void) => void
    busy?: boolean
    /**
     * Render the primary action disabled with this note (server-derived state,
     * e.g. #571 "a payment reminder already went to the customer today").
     */
    disabledReason?: string
  }
  /** The checkmark: resolve (reminder/notification) or snooze (the rest). */
  ack: () => void
  /** aria-label for the checkmark button. */
  ackLabel: string
}

/**
 * Needs-you kinds whose dismissal is recorded server-side (#553). Mirrors
 * `DISMISSIBLE_ITEM_KINDS` in the API's needs-you persistence plus
 * `conversation`, which takes the evidence-snapshot path. Reminders and
 * notifications are absent on purpose: they have real backend acks (done /
 * read), so a dismissal row would be a second, competing source of truth.
 */
const SERVER_DISMISSIBLE_KINDS = new Set([
  'conversation',
  'relay',
  'invoice_overdue',
  'invoice_review',
  'profile_gap',
])

// Stable params objects so the query keys hash identically to other consumers
// (conversations page uses the same {limit:100, filter} shape → shared cache).
const CONV_PARAMS = { limit: 100, filter: 'all' as const }
const OPEN_REMINDERS = { done: false }

/** #571 — the note shown when today's reminder already went to the customer
 *  (or, `failed`, when today's text could not be delivered — nothing was sent). */
/**
 * the owner-visible fact, on the button that acts on it: "reminded 3h ago".
 * Not a disabled reason — nothing here disables the send any more. The 24h
 * cooldown constrains the automatic scanner, and the owner overrides it by
 * pressing the button, so what the owner needs is the information, not a lock.
 */
export function remindedAgoNote(lastReminderSentAt: string | null | undefined): string | null {
  if (!lastReminderSentAt) return null
  return `reminded ${formatRelative(lastReminderSentAt)}`
}

function wholeDaysPast(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000))
}

/** Where a portal notification's row navigates — shared with QueueCard. */
export function notificationHref(
  entityType: string | null,
  entityId: string | null,
): string | null {
  if (!entityType || !entityId) return '/notifications'
  switch (entityType) {
    case 'job':
      return `/jobs/${entityId}`
    case 'invoice':
      return `/invoices/${entityId}`
    case 'conversation':
      return `/messages/${entityId}`
    default:
      return '/notifications'
  }
}

interface UseNeedsYouOptions {
  onInvoiceSending?: (invoice: WebInvoice) => void
}

export function useNeedsYou(accountId: string, options: UseNeedsYouOptions = {}) {
  const { onInvoiceSending } = options
  const queryClient = useQueryClient()
  const { addToast } = useToast()
  const { user } = useAuth()
  // Role only — invoicing is not tier-gated, so there is no plan
  // condition that can hide an invoice waiting on the owner's approval.
  const canApproveInvoices = userCan(user, 'canGenerateInvoices')

  const { data: account } = useAccount(accountId)
  const {
    data: notifications,
    isLoading: notifLoading,
    isError: notifError,
    refetch: refetchNotifications,
  } = useNotifications(accountId)
  const { data: unreadCount = 0 } = useUnreadCount(accountId)
  const {
    data: convData,
    isLoading: convLoading,
    isError: convError,
    refetch: refetchConversations,
  } = useConversations(CONV_PARAMS)
  const {
    data: invoiceData,
    isLoading: invLoading,
    isError: invError,
    refetch: refetchInvoices,
  } = useAllInvoices(accountId)
  const {
    data: reminders,
    isLoading: remLoading,
    isError: remError,
    refetch: refetchReminders,
  } = useReminders(accountId, OPEN_REMINDERS)

  const { mutate: markRead } = useMarkRead()
  const { mutate: markAllRead, isPending: markAllPending } = useMarkAllRead()
  const { mutate: updateReminder } = useUpdateReminder()
  const {
    mutate: sendInvoiceReminder,
    isPending: reminderPending,
    variables: reminderInvoiceId,
  } = useSendInvoiceReminder()
  const {
    mutate: approveInvoice,
    isPending: approvalPending,
    variables: approvingInvoiceId,
  } = useApproveInvoice()

  // Server-side dismissals (#553). The localStorage store below stays as
  // an OPTIMISTIC CACHE — it hides the row on this device the instant the
  // checkmark is pressed, before the POST lands — but the server set is what
  // makes a dismissal follow the contractor from the phone to the laptop.
  const { data: serverDismissals } = useNeedsYouDismissals(accountId)
  const { mutate: postDismissal } = useRecordNeedsYouDismissal()
  const dismissedRevisions = useMemo(() => {
    const map = new Map<string, string>()
    for (const row of serverDismissals?.dismissals ?? []) map.set(row.itemKey, row.sourceEventAt)
    return map
  }, [serverDismissals])

  // Checkmark state: `snoozed` is the local optimistic dismiss for kinds with
  // no backend ack (conversations/invoices); `ackedIds` hides a just-acked row
  // instantly so it can't pop back while its mutation round-trips.
  const [snoozed, setSnoozedState] = useState<Record<string, NeedsYouSnoozeRecord>>(() =>
    readSnoozed(accountId),
  )
  const [ackedIds, setAckedIds] = useState<ReadonlySet<string>>(() => new Set<string>())
  const snoozeAccountRef = useRef(accountId)
  useEffect(() => {
    if (snoozeAccountRef.current === accountId) return
    snoozeAccountRef.current = accountId
    setSnoozedState(readSnoozed(accountId))
    setAckedIds(new Set<string>())
  }, [accountId])

  const snoozeItem = useCallback(
    (id: string, at: string) => {
      // Optimistic: hide it here now, so the checkmark feels instant and the row
      // cannot flash back while the POST is in flight.
      setSnoozedState((prev) => {
        const next = { ...prev, [id]: { at, snoozedAtMs: Date.now() } }
        writeSnoozed(accountId, next)
        return next
      })
      // …and record it server-side so the other device agrees (#553). Only the
      // kinds the API accepts are posted: reminders and notifications have real
      // backend acks and never reach here. Fire-and-forget with no error toast —
      // a failed POST degrades to exactly the old per-browser behaviour, which is
      // not worth interrupting the contractor over.
      const kind = id.slice(0, id.indexOf(':'))
      if (!SERVER_DISMISSIBLE_KINDS.has(kind)) return
      postDismissal({
        resolutionType: 'DISMISSED',
        itemKey: id,
        sourceEventAt: at,
        ...(kind === 'conversation' ? { conversationId: id.slice(id.indexOf(':') + 1) } : {}),
      })
    },
    [accountId, postDismissal],
  )
  const hideAcked = useCallback((id: string) => {
    setAckedIds((prev) => new Set(prev).add(id))
  }, [])

  // The notifications list itself doesn't poll — only the 30s unread-count
  // query does. Piggyback on it: when the count moves, the feed's sources are
  // stale, so refresh them instead of adding another interval.
  const prevUnread = useRef(unreadCount)
  useEffect(() => {
    if (prevUnread.current === unreadCount) return
    prevUnread.current = unreadCount
    queryClient.invalidateQueries({ queryKey: ['notifications', accountId] })
    queryClient.invalidateQueries({ queryKey: ['conversations'] })
  }, [accountId, queryClient, unreadCount])

  const stripePaused = Boolean(
    account?.stripeAccountId && (!account.stripeChargesEnabled || !account.stripePayoutsEnabled),
  )

  const { items, t3Items, candidateIds } = useMemo(() => {
    const t12: NeedsYouItem[] = []
    const t3: NeedsYouItem[] = []
    // Entities already represented by a live (state-derived) row — any unread
    // notification pointing at one of these is a duplicate signal, drop it.
    const emitted = new Set<string>()
    // Every id the sources produced this pass, hidden or not — the prune
    // effect uses it to drop snooze/ack entries for genuinely resolved items.
    const candidates = new Set<string>()
    const nowMs = Date.now()
    const hidden = (id: string, at: string) => {
      candidates.add(id)
      if (ackedIds.has(id)) return true
      // A server dismissal is scoped to the revision it was made at, so new
      // activity (a new `at`) surfaces the card again on every device — the
      // same rule the local snooze used, now shared.
      if (dismissedRevisions.get(id) === at) return true
      return isNeedsYouSnoozeActive(at, snoozed[id], nowMs)
    }

    const accountPhones = {
      tenFourPhone: account?.phone ?? null,
      ownerPhone: account?.ownerPhone ?? null,
    }
    const conversations = (convData?.items ?? []).filter((c) =>
      isCustomerFacingConversation(c, accountPhones),
    )
    const invoiceConversationFor = (invoice: WebInvoice) => {
      const textThreads = conversations.filter((conversation) => conversation.type === 'SMS')
      if (invoice.customerId) {
        const customerThread = textThreads.find(
          (conversation) => conversation.customerId === invoice.customerId,
        )
        if (customerThread) return customerThread
      }
      if (invoice.customerPhone) {
        return textThreads.find((conversation) =>
          phonesMatch(conversation.customerPhone, invoice.customerPhone),
        )
      }
      return undefined
    }
    for (const c of conversations) {
      const candidate = conversationNeedsYouCandidate(c)
      if (!candidate) continue
      const { itemId: id, at } = candidate
      // Still counts as emitted while snoozed — its duplicate notification
      // must not resurface a row the contractor just dismissed.
      emitted.add(id)
      if (hidden(id, at)) continue
      const name = c.customerName?.trim() || formatPhone(c.customerPhone ?? '') || 'A customer'
      t12.push({
        id,
        kind: 'conversation',
        conversationKind: conversationActivityKind(c),
        conversationId: c.id,
        conversation: c,
        tier: 1,
        // One bold summary line (thicker Inter); the expanded thread carries
        // the message preview — no second row under the name.
        title: `${name} is waiting for a reply`,
        at,
        href: `/messages/${c.id}`,
        action: { label: 'Reply', href: `/messages/${c.id}` },
        ack: () => snoozeItem(id, at),
        ackLabel: 'Dismiss',
      })
    }

    for (const inv of invoiceData ?? []) {
      // invoiceAttention classifies on RAW status: a DRAFT still needs the
      // contractor's approval even though its display status is also "Draft".
      const attention = invoiceAttention(inv)
      const invoiceConversation = invoiceConversationFor(inv)
      if (attention === 'overdue') {
        emitted.add(`invoice:${inv.id}`)
        const id = `invoice_overdue:${inv.id}`
        const at = inv.overdueAt ?? inv.sentAt ?? inv.createdAt
        if (hidden(id, at)) continue
        const invoiceId = inv.id
        const days = inv.overdueAt ? wholeDaysPast(inv.overdueAt) : 0
        const remindedAgo = remindedAgoNote(inv.lastReminderSentAt)
        t12.push({
          id,
          kind: 'invoice_overdue',
          conversationId: invoiceConversation?.id,
          conversation: invoiceConversation,
          invoice: inv,
          tier: 1,
          title: `${formatCurrency(inv.balanceDue ?? inv.total)} overdue — ${inv.customerName}`,
          sub: `${formatInvoiceNo(inv.invoiceNumber)}${days > 0 ? ` · ${days} day${days === 1 ? '' : 's'} past due` : ''}`,
          at,
          href: `/invoices/${inv.id}`,
          // #570: the payment nudge is a customer-facing send, gated like
          // "Approve & send" (approve_invoices) — a member without it gets the
          // read path only.: the button is never disabled by the cooldown
          // (that window is the automatic scanner's, and the owner overrides
          // it); it carries the fact instead — "Send reminder · reminded 3h ago".
          action: !canApproveInvoices
            ? { label: 'View invoice', href: `/invoices/${inv.id}` }
            : {
                label: remindedAgo ? `Send reminder · ${remindedAgo}` : 'Send reminder',
                busy: reminderPending && reminderInvoiceId === invoiceId,
                run: (onResolved) =>
                  sendInvoiceReminder(invoiceId, {
                    onSuccess: (result) => {
                      if (result.queued) {
                        addToast('success', "Reminder queued — we'll text the customer")
                        if (onResolved) onResolved()
                        else snoozeItem(id, at)
                      } else {
                        // The same press, repeated inside 60s. Nothing was sent
                        // and nothing is blocked — a minute from now it works.
                        addToast('info', 'Already sending that reminder — give it a moment')
                      }
                    },
                    onError: () => addToast('error', 'Couldn’t send the reminder'),
                  }),
              },
          ack: () => snoozeItem(id, at),
          ackLabel: 'Dismiss',
        })
      } else if (attention === 'awaiting_ok') {
        emitted.add(`invoice:${inv.id}`)
        const id = `invoice_review:${inv.id}`
        const at = inv.createdAt
        if (hidden(id, at)) continue
        t12.push({
          id,
          kind: 'invoice_review',
          invoice: inv,
          tier: 2,
          title: `Invoice for ${inv.customerName} is waiting on your OK`,
          sub: `${formatInvoiceNo(inv.invoiceNumber)} · ${formatCurrency(inv.total)}`,
          at,
          href: `/invoices/${inv.id}`,
          action: canApproveInvoices
            ? {
                label: 'Approve & send',
                busy: approvalPending && approvingInvoiceId === inv.id,
                run: (onResolved) =>
                  approveInvoice(inv.id, {
                    onSuccess: () => {
                      addToast('success', `Invoice is on its way to ${inv.customerName}`)
                      onInvoiceSending?.(inv)
                      onResolved?.()
                    },
                    onError: (error) =>
                      addToast(
                        'error',
                        error instanceof Error && error.message
                          ? error.message
                          : 'Couldn’t approve the invoice',
                      ),
                  }),
              }
            : { label: 'Review', href: `/invoices/${inv.id}` },
          ack: () => snoozeItem(id, at),
          ackLabel: 'Dismiss',
        })
      }
    }

    // #486's derived "Finish your business profile" card USED TO BE EMITTED
    // HERE. **This removes it from Home**, and this note stays so the
    // next reader does not "restore" a card whose absence is the ruling.
    //
    // This is not overruled — its point was that completeness is DERIVED from
    // the account row rather than stored as a seeded reminder, and that is still
    // exactly how it works. What changed is WHERE the derived item surfaces.
    // This draws a boundary Home did not have: **a setup-class item must not
    // also be a Home needs-you row.** Home is for what needs the owner today —
    // a job, an invoice, a customer waiting. A task that has sat there since
    // signup teaches them the list is partly noise, and that is the one thing
    // this list cannot afford.
    //
    // So the item moved rather than died: it is `finish-profile` in the sidebar
    // setup checklist, derived from the same `profileGaps` rule via
    // `SETUP_CHECKLIST_ITEMS`, dismissible, and self-clearing the moment the
    // gaps close — every property wanted, on the surface assigns it.
    // See `packages/domain/src/account/setup-checklist.ts`.

    for (const r of reminders ?? []) {
      if (!r.dueAt || !isReminderDue(r.dueAt)) continue
      // the second half: the four `outreach-setup:…` seeds are the old
      // wizard's to-do list wearing a reminder's clothes, and setup now lives
      // in the sidebar checklist. Skipped BEFORE `emitted`, so nothing
      // downstream counts them as shown — the row is not hidden, it is not a
      // Home row at all. They remain real reminders on /reminders, which is
      // the owner's own list and not a triage feed.
      if (isSetupClassReminderId(r.id)) continue
      emitted.add(`reminder:${r.id}`)
      const id = `reminder:${r.id}`
      if (hidden(id, r.dueAt)) continue
      const reminderId = r.id
      const onboardingTarget = onboardingReminderTarget(r.id)
      t12.push({
        id,
        kind: 'reminder',
        reminder: r,
        tier: 2,
        title: r.text,
        sub: r.customer?.name
          ? `For ${r.customer.name}`
          : r.job?.customerName
            ? `For ${r.job.customerName}’s job`
            : undefined,
        at: r.dueAt,
        // A seeded first-run reminder ("Connect payments") is an instruction,
        // and an instruction with no destination is where a brand-new owner
        // stalls (#487). Send the whole row to the page that completes it, and
        // give it the action button too, so the card is actionable at a glance.
        href: onboardingTarget?.href ?? '/reminders',
        ...(onboardingTarget
          ? { action: { label: onboardingTarget.label, href: onboardingTarget.href } }
          : {}),
        // The row checkmark replaced the old inline "Done" pill.
        ack: () => {
          hideAcked(id)
          updateReminder({ id: reminderId, accountId, updates: { done: true } })
        },
        ackLabel: 'Mark done',
      })
    }

    for (const n of notifications ?? []) {
      if (n.read) continue
      if (
        n.eventType === 'assistant.handoff_requested' &&
        n.handoff?.links.conversationId &&
        emitted.has(`conversation:${n.handoff.links.conversationId}`)
      ) {
        // The active customer conversation already owns the top-priority row.
        // Keep this unread so the handoff surfaces after that row settles.
        continue
      }
      if (n.entityType && n.entityId && emitted.has(`${n.entityType}:${n.entityId}`)) continue
      const href = notificationHref(n.entityType, n.entityId)
      const notificationId = n.id
      const markThisRead = () => markRead({ id: notificationId, accountId })
      if (n.eventType === 'assistant.handoff_requested' && n.handoff) {
        const id = `handoff:${n.handoff.id}`
        if (hidden(id, n.handoff.requestedAt)) continue
        const href = `/aria/${n.handoff.links.ariaThreadId}`
        t12.push({
          id,
          kind: 'handoff',
          notification: n,
          tier: 1,
          title: 'tenfour needs your input',
          sub: n.handoff.question,
          at: n.handoff.requestedAt,
          href,
          action: { label: 'Open in Brain', href },
          ack: () => {
            hideAcked(id)
            markThisRead()
          },
          ackLabel: 'Mark read',
        })
      } else if (n.eventType === 'customer.relay') {
        const id = `relay:${n.id}`
        if (hidden(id, n.createdAt)) continue
        t12.push({
          id,
          kind: 'relay',
          notification: n,
          tier: 1,
          title: n.title,
          sub: n.body,
          at: n.createdAt,
          href,
          action: { label: 'Open', href: href ?? undefined },
          ack: () => {
            hideAcked(id)
            markThisRead()
          },
          ackLabel: 'Mark read',
        })
      } else {
        const id = `notification:${n.id}`
        if (hidden(id, n.createdAt)) continue
        t3.push({
          id,
          kind: 'notification',
          notification: n,
          tier: 3,
          title: n.title,
          sub: n.body || undefined,
          at: n.createdAt,
          href,
          onOpen: markThisRead,
          ack: () => {
            hideAcked(id)
            markThisRead()
          },
          ackLabel: 'Mark read',
        })
      }
    }

    t3.sort((a, b) => b.at.localeCompare(a.at))
    return { items: sortNeedsYouItems(t12), t3Items: t3, candidateIds: candidates }
  }, [
    account?.phone,
    account?.ownerPhone,
    accountId,
    ackedIds,
    addToast,
    approvalPending,
    approveInvoice,
    approvingInvoiceId,
    canApproveInvoices,
    account,
    convData?.items,
    dismissedRevisions,
    hideAcked,
    invoiceData,
    markRead,
    notifications,
    onInvoiceSending,
    reminders,
    reminderInvoiceId,
    reminderPending,
    sendInvoiceReminder,
    snoozed,
    snoozeItem,
    updateReminder,
  ])

  const isLoading = notifLoading || convLoading || invLoading || remLoading

  // DES-01: any failed source means the feed may be lying by omission — the
  // card must say so instead of rendering the calm empty/partial state.
  const isError = notifError || convError || invError || remError
  const refetch = useCallback(() => {
    if (notifError) void refetchNotifications()
    if (convError) void refetchConversations()
    if (invError) void refetchInvoices()
    if (remError) void refetchReminders()
  }, [
    convError,
    invError,
    notifError,
    refetchConversations,
    refetchInvoices,
    refetchNotifications,
    refetchReminders,
    remError,
  ])

  // Once an item disappears from its source (genuinely resolved), drop its
  // snooze/ack bookkeeping. Never prune mid-load OR mid-error — empty sources
  // would look like "everything resolved" and wipe live snoozes.
  useEffect(() => {
    if (isLoading || isError) return
    setSnoozedState((prev) => {
      const stale = Object.keys(prev).filter((id) => !candidateIds.has(id))
      if (stale.length === 0) return prev
      const next = { ...prev }
      for (const id of stale) delete next[id]
      writeSnoozed(accountId, next)
      return next
    })
    setAckedIds((prev) => {
      const keep = [...prev].filter((id) => candidateIds.has(id))
      return keep.length === prev.size ? prev : new Set(keep)
    })
  }, [accountId, candidateIds, isError, isLoading])

  return {
    items,
    t3Items,
    count: items.length,
    stripePaused,
    isLoading,
    isError,
    refetch,
    markAllRead: () => markAllRead(accountId),
    markAllPending,
  }
}
